import { FetchError } from 'ofetch';
import { defineStore } from 'pinia';
import jwtDecode from 'jwt-decode';
import { useGtm } from '@gtm-support/vue-gtm';
import {
  DELIVERY_EXPRESS,
  EXPRESS_DELIVERIES,
  STORE_EXPRESS,
  DELIVERY_EXPRESS_OBJECT,
} from '../config/delivery-config';
import { PUNCHOUT_SESSION_STORAGE_KEY, useUiStore } from './ui';
import { events } from './state-machines/cartMachine';
import { useDeliveryStore } from './delivery';
import { useStoresStore } from './stores';
import { useContentStore } from './content';
import { useAssortmentStore } from './assortment';
import { useCustomerStore } from './customer';
import { useCartStore } from './cart';
import { useCampaignStore } from './campaign';
import { useAddressStore } from './address';
import { useUsersStore } from './users';
import { useOrderStore } from './order';
import { useTrackingStore } from './tracking';
import { useBrandStore } from './brand';
import { PERMISSION_DENIES } from '@/config/permission-config.js';
import { FavouriteItem, SessionTokenPayload, User } from '@/types/user';
import { AuthenticationData } from '~/types';
import { LocationQuery } from '#vue-router';
import { StoreLocation } from '~/types/store';

const { AUTHENTICATION_SUCCESS, AUTHENTICATION_FAILED, NOT_AUTHENTICATED } = OnSessionStartedCodes;

export const REFRESH_TOKEN = 'refresh-token';
export const CURRENT_TOKEN = 'current-token';

type UserFavorite = {
  articleNumber: string;
  favouriteId?: number;
};

export type LoginPayload = {
  refreshToken?: string;
  username?: string;
  password?: string;
  email?: string;
  transferCart?: boolean;
};

type UpdateUserPayload = {
  defaultCustomerId?: string;
  defaultDeliveryMethod?: string;
  defaultProjectId?: string | null;
  firstName?: string;
  lastName?: string;
  mobileNumber?: string;
  preferredStore?: string;
  userId?: string;
  preferredDeliveryMethod?: string;
};

export type UpdateRequestPayload = UpdateUserPayload & User;

type FetchUserError = {
  error?: unknown;
  status?: number;
};

type NuxtRuntimeFeatures = {
  FEATURE_ORDER_REQUEST: boolean;
  FEATURE_FIXED_DELIVERY: boolean;
};

const defaultUser = {
  userId: '',
  isEmailVerified: false,
  firstName: '',
  isPhoneNumberVerified: false,
  mobileNumber: '',
  lastName: '',
  email: '',
  customers: [],
  customerPermissions: [],
  preferredStore: '',
  preferredDeliveryMethod: '',
  roles: [],
  active: false,
  defaultDeliveryMethod: 'DBI',
};

export const helpers = {
  isAllowed(denies: Set<string>, permissionDeny: string): boolean {
    return !denies.has(permissionDeny);
  },
};

export const useUserStore = defineStore('user', () => {
  const isAuthenticating = ref<boolean>(false);
  const onSessionStartedHooks = ref<Function[]>([]);
  const currentUser = ref<User>({ ...defaultUser });
  const isAdmin = ref<boolean>(false);
  const currentRole = ref<string>('');
  const currentToken = ref<string>('');
  const refreshToken = ref<string>('');
  const isFetchingFavorites = ref<boolean>(false);
  const favorites = ref<UserFavorite[]>([]);
  const denies = ref<Set<string>>(new Set());
  const permissionDeniesHaveBeenChecked = ref<boolean>(false);

  const onSessionStarted = (statusCode: number): void => {
    onSessionStartedHooks.value.forEach((cb) => {
      cb(statusCode);
    });
    onSessionStartedHooks.value = [];
  };

  const registerSessionStartedHook = (cb: Function): void => {
    onSessionStartedHooks.value.push(cb);
  };

  const unregisterSessionStartedHook = (cb: Function): void => {
    onSessionStartedHooks.value = onSessionStartedHooks.value.filter((hook) => hook !== cb);
  };

  const setIsAuthenticating = (value: boolean): void => {
    isAuthenticating.value = value;
  };

  const setCurrentToken = (payload: string): void => {
    if (!payload) return;
    const { $sentry } = useNuxtApp();

    let currentTokenPayload: SessionTokenPayload;
    try {
      currentTokenPayload = jwtDecode(payload.replace('Bearer ', ''));
    } catch (error) {
      // If the token is invalid, remove it from local storage to prevent additional errors
      localStorage.removeItem(CURRENT_TOKEN);
      $sentry.captureException(error);
      // eslint-disable-next-line no-console
      console.error('An error occured trying to decode the token', error);
      return;
    }

    currentToken.value = payload;
    localStorage.setItem(CURRENT_TOKEN, payload);

    const role = currentTokenPayload.customerPermissions.role as Role;
    if (
      !helpers.isAllowed(
        new Set(currentTokenPayload.customerPermissions.permissionDenies),
        PERMISSION_DENIES.LIMITED_DELIVERIES,
      )
    ) {
      currentUser.value.preferredDeliveryMethod = DELIVERY_EXPRESS_OBJECT.id;
    }
    currentUser.value.userId = currentTokenPayload.username;
    currentRole.value = role;
    isAdmin.value = verifyRoleAdmin(role);
  };

  const setRefreshToken = (payload = ''): void => {
    if (!payload) return;

    refreshToken.value = payload;
    localStorage.setItem(REFRESH_TOKEN, payload);
  };

  const setCurrentUser = (user: User): void => {
    const gtm = useGtm();
    if (!user) return;
    currentUser.value = { ...currentUser.value, ...user };
    gtm?.trackEvent({
      event: 'login',
      userId: currentUser.value.userId,
      visitorType: 'loggedIn',
    });
  };

  const startSession = async (queryParams?: LocationQuery): Promise<{ data?: string; error?: any } | undefined> => {
    if (process.server) {
      return;
    }
    const { setDeliveryOptions } = useDeliveryStore();
    const { isPunchout } = useUiStore();
    const { public: config } = useRuntimeConfig();
    const { brandConfig } = useBrandStore();

    setDeliveryOptions(brandConfig.deliveryOptions);
    const storedToken = localStorage.getItem(CURRENT_TOKEN) || '';
    const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN) || '';

    /* Replace v1 tokens
     * Can be removed when we are sure no more valid v1 tokens exist
     */
    if (isTokenValid(storedToken, config.users.tokenRefreshMargin)) {
      const decodedStoredToken: SessionTokenPayload = jwtDecode(storedToken);
      if (decodedStoredToken?.version === 1) {
        login({ refreshToken: refreshToken.value }, isPunchout);
        return;
      }
    }
    /* Replace v1 tokens end */

    setCurrentToken(storedToken);
    setRefreshToken(storedRefreshToken);

    const { initiateTrackingId } = useTrackingStore();
    initiateTrackingId();
    if (isTokenValid(currentToken.value, config.users.tokenRefreshMargin)) {
      isAuthenticating.value = true;
      const fetchUserError = await fetchUserData();
      isAuthenticating.value = false;
      if (fetchUserError) return login({ refreshToken: refreshToken.value }, false);
      afterLogin();
    } else if (isPunchout) {
      const { logonId, logonPassword, email } = queryParams!;
      const punchoutAuth = {
        username: logonId as string,
        password: logonPassword as string,
        email: email as string,
      };
      login(punchoutAuth, isPunchout);
    } else if (refreshToken.value) {
      login({ refreshToken: refreshToken.value });
    } else {
      onSessionStarted(NOT_AUTHENTICATED);
    }
  };

  const clear = (): void => {
    currentToken.value = '';
    refreshToken.value = '';
    currentUser.value = defaultUser;
    isAdmin.value = false;
    currentRole.value = '';

    localStorage.removeItem(CURRENT_TOKEN);
    localStorage.removeItem(REFRESH_TOKEN);
    localStorage.removeItem(PUNCHOUT_SESSION_STORAGE_KEY);
  };

  const afterLogin = async (payload?: { transferCart?: boolean }): Promise<void> => {
    const {
      clearState,
      setZipCodeFromAddress,
      setPreSelectedLocations,
      setDeliveryDays,
      lateDeliveryExpressIsActive,
      setSelectedDeliveryId,
    } = useDeliveryStore();
    const { brandConfig } = useBrandStore();
    const { setStoreListFromLocationApi, getStore } = useStoresStore();
    const { fetchAssortmentId } = useAssortmentStore();
    const { showMessage } = useUiStore();
    const { fetchCustomers, setCurrentCustomer } = useCustomerStore();
    const { fetchFooterStoreDetails } = useContentStore();
    const { cartId, fetchCart, transferCart } = useCartStore();
    const { fetchCustomerCampaigns } = useCampaignStore();
    const { $router, $i18n, $authService, $sentry } = useNuxtApp();
    const { public: config } = useRuntimeConfig();

    const { preferredStore, preferredDeliveryMethod, firstName, lastName, email } = currentUser.value;
    clearState();
    const storeList = await setStoreListFromLocationApi();

    if (!storeList?.length) {
      logout();
      if ($router.currentRoute.value.name !== 'index') $router.push({ name: 'index' });
      showMessage({
        text: `${$i18n.t('errors.technical_issues')} ${$i18n.t('errors.try_again_later')}`,
      });

      return;
    }

    if (preferredStore) {
      await setPreSelectedLocations({ preferredStore });
    }
    const decoded: SessionTokenPayload = jwtDecode<SessionTokenPayload>(currentToken.value);
    const { customerId, projectId } = decoded.customerPermissions;
    await fetchCustomers();
    setCurrentCustomer(decoded.customerPermissions);

    if ((config.features as NuxtRuntimeFeatures).FEATURE_FIXED_DELIVERY) {
      const { error, data } = await $authService.getCustomersDeliveryDays(customerId);
      // We get a 404 when a customer does not have delivery days set, no need to report to Sentry
      if (error && error?.response?.status !== 404) {
        $sentry.captureException(error);
      }
      // Set the delivery days for the customer, will be null if unused
      setDeliveryDays(data || null);
    }

    fetchFooterStoreDetails({ store: getStore(preferredStore || brandConfig.defaultStoreId).value });

    if (currentToken.value) {
      const currentTokenPayload: SessionTokenPayload = jwtDecode<SessionTokenPayload>(
        currentToken.value.replace('Bearer ', ''),
      );
      denies.value = new Set(currentTokenPayload.customerPermissions.permissionDenies);
      permissionDeniesHaveBeenChecked.value = true;
    }

    // Check if customer is only allowed express delivery, set selectedDeliveryId to EXB if that is the case,
    // else set to preferredDeliveryMethod or fall back to defaultDeliveryId
    const isExpressDeliveryOnly =
      !isAllowed(PERMISSION_DENIES.LIMITED_DELIVERIES).value && !EXPRESS_DELIVERIES.includes(preferredDeliveryMethod);

    const selectedExpressDeliveryOption = lateDeliveryExpressIsActive ? DELIVERY_EXPRESS : STORE_EXPRESS;

    setSelectedDeliveryId(
      isExpressDeliveryOnly ? selectedExpressDeliveryOption : preferredDeliveryMethod || brandConfig.defaultDeliveryId,
    );

    fetchUserFavorites();
    fetchAssortmentId();

    await fetchCart();
    if (payload?.transferCart) transferCart(cartId!);

    fetchCustomerCampaigns();
    setZipCodeFromAddress();
    onSessionStarted(AUTHENTICATION_SUCCESS);

    const chatUserContext = projectId ? `${customerId}/${projectId}` : customerId;
    const eventDetails = {
      name: `${firstName} ${lastName} (${$i18n.t('cost_place_short')}: ${chatUserContext})`,
      email,
    };
    const event = new CustomEvent('userLoggedIn', { detail: eventDetails });
    window.dispatchEvent(event);
  };

  const login = async (
    payload: LoginPayload,
    loginAsPunchout = false,
  ): Promise<{ data?: string; error?: FetchError | FetchUserError }> => {
    const { showMessage } = useUiStore();
    const { $authService } = useNuxtApp();

    const { transferCart: shouldTransfer, ...authData } = payload;

    isAuthenticating.value = true;
    const options = { isPunchout: loginAsPunchout };
    let data: AuthenticationData;
    try {
      data = await $authService.login(authData, options);

      const { AuthenticationResult, ChallengeName } = data;

      if (ChallengeName) {
        return { data: ChallengeName };
      } else {
        setCurrentToken('Bearer ' + AuthenticationResult.AccessToken);
        // When using the refresh token to login we will not get another refresh token back.
        if (AuthenticationResult.RefreshToken) {
          setRefreshToken(AuthenticationResult.RefreshToken);
        }
      }
      const fetchUserError = await fetchUserData();
      if (fetchUserError) return { error: fetchUserError };
      afterLogin({ transferCart: shouldTransfer });

      return { data: 'Successful login' };
    } catch (error: unknown) {
      logout();
      if (error instanceof FetchError && error?.response?._data === 'Invalid token') {
        showMessage({ text: 'errors.invalid_token' });
      }
      onSessionStarted(AUTHENTICATION_FAILED);
      return { error: error as FetchError };
    } finally {
      isAuthenticating.value = false;
    }
  };

  const fetchUserData = async (): Promise<FetchUserError | undefined> => {
    const { getAvailableDeliveryOptions } = useDeliveryStore();
    const { brandConfig } = useBrandStore();
    const { $authService } = useNuxtApp();
    try {
      const data = await $authService.getCurrentUser(currentUser.value.userId, currentToken.value);
      const { defaultDeliveryMethod, ...user } = data;

      // Validate that the default delivery method is valid for this brand
      user.preferredDeliveryMethod = getAvailableDeliveryOptions.some((option) => option.id === defaultDeliveryMethod!)
        ? defaultDeliveryMethod!
        : brandConfig.defaultDeliveryId;

      setCurrentUser(user);
    } catch (error: unknown) {
      clear();
      isAuthenticating.value = false;
      // account flyout expects a certain set of status codes
      return { error, status: 403 };
    }
  };

  const logout = async (): Promise<void> => {
    const { $sentry } = useNuxtApp();
    const { resetSearchParams } = useUsersStore();
    const { setCartItems, setQuickOrderVariants, setCartId, setCartStatus } = useCartStore();
    const { clear: clearCustomer } = useCustomerStore();
    const { clearState: clearDelivery } = useDeliveryStore();
    const { clearState: clearOrder } = useOrderStore();
    const { clearAssortment } = useAssortmentStore();
    const { clearModeState } = useUiStore();
    const { setAddressesCached } = useAddressStore();

    const error = await invalidateToken();
    clear();
    resetSearchParams();
    clearCustomer();
    clearDelivery();
    clearOrder();
    setCartItems([]);
    setQuickOrderVariants([]);
    setCartId(undefined);
    setCartStatus(events.LOGOUT);
    clearAssortment();
    favorites.value = [];
    clearModeState();
    setAddressesCached(false);

    if (error) {
      // Logout endpoint returns 401 when you send a request with an expired token
      // We don't want it to appear in Sentry because it's not a real issue
      if (error instanceof FetchError && error.statusCode === 401) return;

      $sentry.captureException(error);
    }
  };

  const invalidateToken = async (): Promise<unknown | undefined> => {
    const { $authService } = useNuxtApp();
    if (!currentUser.value.userId || !currentToken.value) return;
    try {
      return await $authService.logout(currentUser.value.userId, currentToken.value);
    } catch (error) {
      return error;
    }
  };

  const updateUserData = async (payload: UpdateUserPayload = {}): Promise<undefined | FetchError> => {
    const { $authService } = useNuxtApp();
    const { setSelectedDeliveryId } = useDeliveryStore();
    const userId = payload.userId || currentUser.value.userId;

    const wantedKeys = [
      'firstName',
      'lastName',
      'mobileNumber',
      'preferredStore',
      'defaultCustomerId',
      'defaultProjectId',
    ];

    const currentUserData = pluck(currentUser.value, wantedKeys) as User;
    const sanitizedPayload = pluck(payload, wantedKeys) as UpdateUserPayload;

    const requestPayload: UpdateRequestPayload = { ...currentUserData, ...sanitizedPayload };

    const { error } = await $authService.updateUser(userId, requestPayload);
    if (error) return error;

    setCurrentUser({ ...currentUser.value, ...sanitizedPayload });

    if (
      payload.preferredDeliveryMethod &&
      payload.preferredDeliveryMethod !== currentUser.value.preferredDeliveryMethod
    ) {
      setSelectedDeliveryId(payload.preferredDeliveryMethod);
    }
  };

  const fetchCurrentUserPermissions = async (): Promise<void> => {
    const { fetchUserPermissions } = useUsersStore();
    const currentUserPermissions = await fetchUserPermissions({
      userId: currentUser.value.userId,
      params: { active: 1, virtualContext: 0 },
    });

    if (!currentUserPermissions) return;

    setCurrentUser({
      ...currentUser.value,
      customerPermissions: currentUserPermissions.customerPermissions!,
    });
  };

  const fetchUserFavorites = async (): Promise<void> => {
    const { $authService } = useNuxtApp();
    isFetchingFavorites.value = true;
    const { data, error } = await $authService.getUserFavorites({ params: { limit: 50 } });
    isFetchingFavorites.value = false;

    if (error || !data) return;

    const favoritesList = data.map<UserFavorite>((item: FavouriteItem) => ({
      articleNumber: item.articleNumber,
      favouriteId: item.favouriteId,
    }));

    favorites.value = favoritesList;
  };

  const deleteFavorites = async ({ favoriteIds }: { favoriteIds: number[] }): Promise<void> => {
    const { addToastMessage } = useUiStore();
    const { $authService, $i18n } = useNuxtApp();
    const { error } = await $authService.deleteUserFavorites(favoriteIds);

    if (error) return;

    addToastMessage({ text: $i18n.t('favorites.deleted', favoriteIds.length) });
    favorites.value = favorites.value.filter((favorite) => !favoriteIds.includes(favorite.favouriteId!));
  };

  const addFavorite = async ({ articleNumber }: { articleNumber: string }): Promise<void> => {
    const { $authService, $i18n } = useNuxtApp();
    const { addToastMessage } = useUiStore();
    if (favorites.value.length === 50) {
      confirm($i18n.t('favorites.info_modal.body'));
      return;
    }

    const { data, error } = await $authService.addFavorite(articleNumber);
    if (error || !data) return;
    favorites.value.push(data);
    addToastMessage({ text: $i18n.t('favorites.added') });
  };

  const isLoggedIn = computed<boolean>(() => {
    const { email } = currentUser.value;
    return !!email;
  });

  const getPreferredStore = computed<StoreLocation>(() => {
    const { defaultStore, storeIdMap } = useStoresStore();
    return storeIdMap[currentUser.value.preferredStore] || defaultStore || {};
  });

  const getCurrentUsersAdminCustomers = computed<string[]>(() => {
    return (
      (currentUser.value.customerPermissions || [])
        .filter((cp: { role: string }) => verifyRoleAdmin(cp.role as Role))
        .map((cp: { customerId: string }) => cp.customerId) || []
    );
  });

  const isAllowed = (permissionDeny: string) =>
    computed<boolean>(() => {
      return helpers.isAllowed(denies.value, permissionDeny);
    });

  return {
    currentUser,
    isAuthenticating,
    isAdmin,
    currentToken,
    refreshToken,
    login,
    logout,
    clear,
    favorites,
    onSessionStarted,
    registerSessionStartedHook,
    unregisterSessionStartedHook,
    setIsAuthenticating,
    setCurrentToken,
    setRefreshToken,
    setCurrentUser,
    fetchUserData,
    afterLogin,
    deleteFavorites,
    addFavorite,
    startSession,
    updateUserData,
    fetchCurrentUserPermissions,
    isAllowed,
    isLoggedIn,
    getPreferredStore,
    getCurrentUsersAdminCustomers,
    permissionDeniesHaveBeenChecked,
    currentRole,
  };
});
